import React, { useState } from "react";
import EAModal from "../../shared/Modal/EAModal";
import EACheckbox from "../../shared/Checkbox/EACheckbox";
import EASelect from "../../shared/EASelect/EASelect";
import EARadio from "../../shared/Radio/EARadio";
import EADivider from "../../shared/EADivider";
import {
  ACADEMY_GROUPING_VARIANT_OPTIONS,
  AcademyGroupingVariant,
  SwimmingExerciseGroup,
  SwimmingFilters,
  SwimmingInstructor,
} from "../../../models/SwimmingScheduleData";

const GROUPING_SELECT_OPTIONS = [
  { value: "", label: "Domyślne" },
  ...ACADEMY_GROUPING_VARIANT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  filters: SwimmingFilters;
  onApply: (filters: SwimmingFilters) => void;
  availableLocations: string[];
  availableInstructors: SwimmingInstructor[];
  availableExerciseGroups: SwimmingExerciseGroup[];
  isWeekly?: boolean;
}

const COURSE_TYPE_OPTIONS: { value: SwimmingFilters["courseType"]; label: string }[] = [
  { value: "ALL", label: "Wszystkie" },
  { value: "SwimmingLessonSemester", label: "Semestralny" },
  { value: "SwimmingLessonDynamic", label: "Dynamiczny" },
];

const SwimmingFiltersModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  filters,
  onApply,
  availableLocations,
  availableInstructors,
  availableExerciseGroups,
  isWeekly = false,
}) => {
  const [draft, setDraft] = useState<SwimmingFilters>(filters);

  React.useEffect(() => {
    if (isOpen) setDraft(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleLocationToggle = (location: string) => {
    const next = draft.locations.includes(location)
      ? draft.locations.filter((l) => l !== location)
      : [...draft.locations, location];
    setDraft({ ...draft, locations: next });
  };

  const handleGroupToggle = (id: string) => {
    const next = draft.exerciseGroups.includes(id)
      ? draft.exerciseGroups.filter((g) => g !== id)
      : [...draft.exerciseGroups, id];
    setDraft({ ...draft, exerciseGroups: next });
  };

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const instructorOptions = [
    { value: "", label: "Wszyscy instruktorzy" },
    ...availableInstructors.map((i) => ({
      value: `${i.objectDb},${i.objectId}`,
      label: `${i.name} ${i.lastName}`.trim(),
    })),
  ];

  return (
    <EAModal
      isOpen={isOpen}
      onClose={onClose}
      title="Filtry"
      acceptButtonLabel="Zapisz"
      cancelButtonLabel="Zamknij"
      onAccept={handleApply}
      desktopWidth="480px"
      stickyFooter
    >
      <div className="swimming-filters-modal">

        <div className="swimming-filters-modal__section">
          <EACheckbox
            name="swimming-only-available"
            checked={draft.onlyAvailable}
            onChange={(checked) => setDraft({ ...draft, onlyAvailable: checked })}
            label="Pokaż tylko dostępne"
          />
        </div>

        {!isWeekly && (
        <div className="swimming-filters-modal__section">
          <EASelect
            label="Grupowanie"
            name="swimming-grouping-variant"
            value={draft.groupingVariant}
            onChange={(e) =>
              setDraft({ ...draft, groupingVariant: e.target.value as AcademyGroupingVariant })
            }
            options={GROUPING_SELECT_OPTIONS}
            fullWidth
          />
        </div>
        )}

        {!isWeekly && <EADivider />}

        <div className="swimming-filters-modal__section">
          <p className="swimming-filters-modal__label">Typ kursu</p>
          <div className="swimming-filters-modal__radio-group">
            {COURSE_TYPE_OPTIONS.map((option) => (
              <EARadio
                key={option.value}
                name="swimming-course-type"
                value={option.value}
                checked={draft.courseType === option.value}
                onChange={(value) =>
                  setDraft({ ...draft, courseType: value as SwimmingFilters["courseType"] })
                }
                label={option.label}
              />
            ))}
          </div>
        </div>

        {availableExerciseGroups.length > 0 && (
          <div className="swimming-filters-modal__section">
            <p className="swimming-filters-modal__label">Grupy zajęciowe</p>
            <div className="swimming-filters-modal__locations">
              {availableExerciseGroups.map((group) => {
                const id = `${group.objectDb},${group.objectId}`;
                return (
                  <EACheckbox
                    key={id}
                    name={`swimming-group-${id}`}
                    checked={draft.exerciseGroups.includes(id)}
                    onChange={() => handleGroupToggle(id)}
                    label={group.displayName}
                  />
                );
              })}
            </div>
          </div>
        )}

        {availableLocations.length > 0 && (
          <div className="swimming-filters-modal__section">
            <p className="swimming-filters-modal__label">Tory</p>
            <div className="swimming-filters-modal__locations">
              {availableLocations.map((location) => (
                <EACheckbox
                  key={location}
                  name={`swimming-location-${location}`}
                  checked={draft.locations.includes(location)}
                  onChange={() => handleLocationToggle(location)}
                  label={location}
                />
              ))}
            </div>
          </div>
        )}

        <div className="swimming-filters-modal__section">
          <EASelect
            label="Instruktor"
            name="swimming-instructor"
            value={draft.instructor}
            onChange={(e) => setDraft({ ...draft, instructor: e.target.value })}
            options={instructorOptions}
            fullWidth
          />
        </div>

      </div>
    </EAModal>
  );
};

export default SwimmingFiltersModal;
